import type { FlairPart } from '../../flair/engine';
import { type PaletteKey, PixelCanvas } from '../../lib';
import { grid, type Part, stack } from '../body';
import type { Worn } from '../headwear';
import { EYES, FACE } from '../looks';
import type { Outfit } from '../outfits';
import { drawnLater } from './art';
import { domeLight, type Ellipse, fromPath, type HeadPicture, inEllipse, mask, type SetFlair } from './mythic';

/**
 * The Luchador set (ADR-0160 amended, mythic): a masked champion of the ring. A luchador's mask, tights and a gold
 * championship belt, and the Main Event.
 *
 * The head is a stretched cloth mask in the team's colour over the whole head, the wearer's own eyes and mouth showing
 * through its holes: each eye hole rimmed in white and flicked back into a gold flame, a white crest edged in gold down
 * its middle from the crown to a point between the eyes, laces criss-crossed up its back, the mouth hole rimmed in white.
 * Its flair: two long ribbons in the team's colour and gold streaming off its laces (the bright moving part at 1:1:
 * they never stop fluttering), a glint running over the crest.
 * The suit is a wrestler's: bare shoulders and arms (in the wearer's skin) taped white at the wrists, a singlet in the
 * team's colour on straps, gold piping down its sides, and over it a huge gold championship belt (black leather, a gold
 * plate with a jewel in the team's colour), the team's tights and tall white boots. Its flair: the belt glinting and
 * glowing, sweat flicked off on the run.
 *
 * Rubric (`assetc mythic luchador` at 5× and 1:1 on grass, lane and night, both teams, idle, run and dead, beside the
 * Magician): head S4 R4 P4 L4 C4 A4 F4, singlet S4 R4 P4 L4 C4 F4. Weakest: the mask is the team's colour on the team's
 * tights; the white crest, the gold flames and the belt part them.
 */

// --- The mask --------------------------------------------------------------------------------------------------------

/** The mask's outline standing; lower on the dead pose, whose face is bigger. */
const SHELL = (lying: boolean): Ellipse => (lying ? [0, 1.5, 20.5, 19] : [0, -0.5, 20.5, 17]);
/** The eye holes (over the look's eyes, `EYES_AT`) and the mouth hole (over its mouth). */
const EYE_NEAR: Ellipse = [10.5, 8, 3.2, 4.2];
const EYE_FAR: Ellipse = [-0.5, 8, 3.2, 4.2];
const MOUTH: Ellipse = [8, 14.2, 6.2, 2.9];
/** The crest down the middle: from the crown to a point between the eyes. */
const CREST = [5, -17, 5, 3.5] as const;
/** The gold flames flicked back off each eye's outer corner (x, y pairs). */
const FLAME_NEAR = [12.5, 4.5, 15.5, 1.5, 19, 0.5, 17, 3.5, 14.5, 6] as const;
const FLAME_FAR = [-2.5, 4.5, -6, 1, -11, -1, -8, 3, -4, 6] as const;

/** Cloth in the team's colour at light `l`. */
const cloth = (l: number): PaletteKey => (l > 0.82 ? 'c' : l > 0.32 ? 'b' : l > 0.02 ? 'B' : '1');

const luchaMask: HeadPicture = (u, v, lx, ly, lying) => {
  const shell = SHELL(lying);
  if (inEllipse(u, v, shell) > 1) return null;
  // The holes: the wearer's own eyes and mouth (standing); shut on the dead pose, whose face lies elsewhere.
  for (const eye of [EYE_NEAR, EYE_FAR]) {
    const e = inEllipse(u, v, eye);
    if (e < 1) return lying ? (Math.abs(v - eye[1]) < 0.8 ? 'k' : 'w') : null;
    if (e < 1.7) return 'w';
  }
  const m = inEllipse(u, v, MOUTH);
  if (m < 1) return lying ? 'k' : null;
  if (m < 1.55) return 'w';
  // The gold flames off the eyes, lit along their tops.
  for (const flame of [FLAME_NEAR, FLAME_FAR]) {
    const d = fromPath(u, v, flame);
    if (d < 1.15) return d < 0.5 ? 'y' : v < flame[1] - 1 ? 'y' : 'Y';
  }
  // The crest: white, edged in gold, narrowing to its point between the eyes.
  const across = Math.abs(u - CREST[0]);
  const half = v < -6 ? 2.6 : 2.6 - ((v + 6) / (CREST[3] + 6)) * 2.2;
  if (v >= CREST[1] && v <= CREST[3] && across < half + 1.1) return across < half ? (u < CREST[0] ? 'w' : '5') : 'Y';
  // The laces up its back: white cords criss-crossing over a dark seam.
  if (u < -15.5 && v > -5 && v < 11) {
    const k = (v + 5) % 4;
    if (Math.abs(u + 17.5 - (k - 2) * 0.9) < 0.6 || Math.abs(u + 17.5 + (k - 2) * 0.9) < 0.6) return '5';
    if (Math.abs(u + 17.5) < 0.8) return 'k';
  }
  // The stretched cloth: a dome lit from the top-left.
  return cloth(domeLight(u, v, shell, lx, ly));
};

/** The mask, pictured alone too (the wardrobe's tile): over the default face, its eyes showing. */
const MASK = (): Worn => {
  const worn = mask(luchaMask);
  return { ...worn, alone: stack(FACE, EYES.round.open, worn.front) };
};

// --- The singlet and the belt ----------------------------------------------------------------------------------------

/** The suit standing (the frame's x 5-56, y 39-57): bare arms, the team's singlet on straps, a gold championship belt. */
function singlet(): Part {
  const [x0, y0] = [5, 39];
  const c = new PixelCanvas(52, 19);
  const at = (x: number, y: number, k: PaletteKey) => c.set(x - x0, y - y0, k);
  const inSuit = (x: number, y: number) => {
    if (y < 41 || y > 57) return false;
    if (x >= 16 && x <= 43) return true;
    for (const [cx, rx] of [
      [12.5, 7.5],
      [47, 6.5],
    ] as const) {
      const [dx, dy] = [(x + 0.5 - cx) / rx, (y + 0.5 - 47) / 5.8];
      if (dx * dx + dy * dy <= 1) return true;
    }
    return false;
  };
  const arm = (x: number) => x < 16 || x > 43;
  for (let y = 41; y <= 57; y++)
    for (let x = 5; x <= 56; x++) {
      if (!inSuit(x, y)) continue;
      const lit = !inSuit(x - 1, y) || !inSuit(x, y - 1);
      const shade = !inSuit(x + 1, y) || x > 41;
      if (arm(x)) {
        // Bare arms in the wearer's skin, white tape round the wrists.
        if (y >= 51) at(x, y, y === 51 ? '5' : x > 43 ? '5' : 'w');
        else at(x, y, shade || x > 43 ? 'S' : 's');
        continue;
      }
      // Bare shoulders and chest over the singlet's top.
      at(x, y, y < 45 ? (shade ? 'S' : 's') : lit ? 'c' : shade ? 'B' : 'b');
    }
  // The singlet's straps over the shoulders, its top edge lit.
  for (let y = 41; y <= 44; y++)
    for (const x of [19, 20, 21, 37, 38, 39]) at(x, y, x === 19 || x === 37 ? 'c' : x > 38 ? 'B' : 'b');
  for (let x = 16; x <= 43; x++) at(x, 45, x > 40 ? 'b' : 'c');
  // Gold piping down its sides.
  for (let y = 46; y <= 57; y++) {
    at(17, y, 'y');
    at(42, y, 'U');
  }
  // The championship belt: black leather with gold studs, a big gold plate on the front.
  for (let x = 16; x <= 43; x++) {
    at(x, 51, x > 40 ? '1' : '2');
    at(x, 52, x % 3 === 0 ? 'Y' : '1');
    at(x, 53, x > 40 ? 'k' : '1');
  }
  for (let y = 48; y <= 56; y++)
    for (let x = 23; x <= 37; x++) {
      const e = ((x + 0.5 - 30) / 7.5) ** 2 + ((y + 0.5 - 52.5) / 4.6) ** 2;
      if (e > 1) continue;
      // The jewel in the team's colour, in a ring of white.
      const j = ((x + 0.5 - 30) / 2.2) ** 2 + ((y + 0.5 - 52.5) / 1.9) ** 2;
      if (j < 1) at(x, y, x < 30 && y < 53 ? 'c' : x > 30 ? 'B' : 'b');
      else if (j < 1.9) at(x, y, 'w');
      else if (e > 0.72) at(x, y, x + y < 80 ? 'Y' : 'U');
      else at(x, y, x + y < 79 ? 'y' : x + y > 84 ? 'U' : 'Y');
    }
  return [c, x0, y0];
}

/** The suit lying (right of the legs, under the head): the hem (left), the belt's plate and jewel, the bare chest. */
const DEAD_SINGLET = grid(`
  ....ssss
  ...sssss
  cbbybbss
  bbbYbbss
  cbbYYbss
  bbYyyYss
  cbYycYbs
  bbYcbYbs
  cbUYYUbs
  bbbUUbbs
  cbbYbbss
  bbb1bbss
  cbb1bbss
  bbbbbbss
  cbbbbbbS
  BBBBBBBS
  BBBBBBSS
  ....SSSS
`);

/** The team's tights and tall white boots. */
const SINGLET = (): Outfit => ({ torso: singlet(), dead: [DEAD_SINGLET, 24, 51], legs: { n: '5', N: '4', m: 'w' } });

// --- The Main Event's gold on every other gun ------------------------------------------------------------------------

/** Gold-plated with black leather (the default gun is its own, `pollinator_luchador`). */
const PLATED = { '3': '1', '4': 'Y', '5': 'y' } as const;

// --- Its flair -------------------------------------------------------------------------------------------------------

const WHITE = 0xffffff;
const YELLOW = 0xfee761;
const GOLD = 0xfeae34;
const SILVER = 0xc0cbdc;

/** A ribbon off the mask's laces: the team's colour, a gold stripe, streaming back and fluttering. */
const ribbon = (y: number, dir: number, n: number, phase: number): FlairPart => ({
  kind: 'chain',
  at: { on: 'head', x: 11, y },
  n,
  seg: 2,
  dir,
  curl: 0.03,
  stiff: 7,
  g: 90,
  drag: 0.07,
  sway: [0.14, 0.8, phase],
  wind: 1500,
  w: [3, 3, 2],
  shape: 'bars',
  tones: ['team', GOLD, 'team', 'teamLight', 'team', GOLD, 'team'],
  rim: 'teamDark',
  lit: 'teamLight',
  tatter: 1,
  layer: 'back',
});

const FLAIR: SetFlair = {
  head: [
    ribbon(30, Math.PI * 0.88, 9, 0),
    ribbon(34, Math.PI * 0.8, 8, 1.7),
    // A glint running down the crest.
    {
      kind: 'twinkle',
      at: { on: 'head', x: 35, y: 18 },
      box: [4, 16],
      n: 1,
      period: [0.7, 1.2],
      tones: [WHITE, YELLOW],
      add: true,
      layer: 'front',
    },
  ],
  outfit: [
    // The belt's plate glinting and glowing.
    {
      kind: 'twinkle',
      at: { on: 'torso', x: 30, y: 52 },
      box: [13, 7],
      n: 1,
      period: [0.6, 1],
      tones: [WHITE, YELLOW],
      add: true,
      layer: 'front',
    },
    {
      kind: 'pulse',
      at: { on: 'torso', x: 30, y: 52 },
      r: [4, 6],
      alpha: [0.15, 0.3],
      period: 1.5,
      tone: GOLD,
      layer: 'front',
    },
    // Sweat flicked off the shoulders on the run.
    {
      kind: 'emit',
      at: { on: 'torso', x: 30, y: 42 },
      box: [26, 3],
      rate: 0,
      run: 0.03,
      v: [-26, 10, -26, -10],
      g: 160,
      drag: 0.2,
      life: [0.25, 0.4],
      size: [1, 1],
      tones: [WHITE, SILVER],
      alpha: [0.9, 0],
      layer: 'front',
    },
  ],
  gun: [
    // The jewel glowing in the plate, a glint across its gold.
    {
      kind: 'pulse',
      at: { on: 'gun', x: 28, y: 20 },
      r: [2.5, 4],
      alpha: [0.25, 0.45],
      period: 1.3,
      tone: 0xe43b44,
      layer: 'gun',
    },
    {
      kind: 'twinkle',
      at: { on: 'gun', x: 36, y: 16 },
      box: [30, 6],
      n: 1,
      period: [0.6, 1.1],
      tones: [WHITE, YELLOW],
      add: true,
      layer: 'gun',
    },
  ],
};

/** The Luchador set (ADR-0160 amended). */
export const LUCHADOR = drawnLater({ head: MASK, outfit: SINGLET, gun: PLATED, flair: FLAIR });
